import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Button,
  DatePicker,
  InputField,
  SearchWithFilter,
  Stepper,
} from '@components/common'
import {
  HtmlEditor,
  Inject,
  IToolbarItems,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
} from '@syncfusion/ej2-react-richtexteditor'
import './MessageCenterFaxComposeStyle.css'
import useFaxCompose from './useFaxCompose'
import { useMessageCenterTabLayer } from '../../MessageCenterTabLayer.context'
import { CoversheetField, IFaxCoversheet } from '@dataTypes/fax.type'
import { formatFileSize } from '@utils/fax.util'
import { previewContentToHtml } from '@utils/fax-coversheet.util'
import { sanitizeEmailHtml } from '@utils/sanitize-html.util'
import { EmptyState } from '@components/ui'
import SelectFaxRecipientModal from './modals/SelectFaxRecipientModal.component'
import InsertFieldModal from './modals/InsertFieldModal.component'
import FaxCoversheetThumbPreview from './FaxCoversheetThumbPreview.component'
import FaxDocumentPagePreview from './FaxDocumentPagePreview.component'

const STEPS = ['New fax', 'Cover sheet', 'Add documents', 'Review']
const COVERSHEET_UPLOAD_ACCEPT = '.pdf,image/*'

type FaxComposeProps = {
  faxRteRef?: RefObject<RichTextEditorComponent | null>
}

const FaxCompose: React.FC<FaxComposeProps> = ({ faxRteRef }) => {
  const internalRteRef = useRef<RichTextEditorComponent>(null)
  const rteRef = faxRteRef ?? internalRteRef
  const faxRteToolbarSlotRef = useRef<HTMLDivElement>(null)
  const hooks = useFaxCompose(rteRef)

  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const resetDocumentDrag = useCallback(() => {
    setDragIndex(null)
    setDragOverIndex(null)
  }, [])

  const relocateFaxRteToolbar = useCallback(() => {
    const rte = rteRef.current
    const slot = faxRteToolbarSlotRef.current
    if (!rte?.element || !slot) return
    const toolbarWrapper = rte.element.querySelector('.e-toolbar-wrapper')
    if (toolbarWrapper && toolbarWrapper.parentElement !== slot) {
      slot.appendChild(toolbarWrapper)
    }
  }, [rteRef])

  const handleFaxRteCreated = useCallback(() => {
    relocateFaxRteToolbar()
  }, [relocateFaxRteToolbar])

  useEffect(() => {
    if (hooks.step === 1 && hooks.coversheetMode === 'template') {
      relocateFaxRteToolbar()
    }
  }, [hooks.step, hooks.coversheetMode, relocateFaxRteToolbar])
  const { setTabLayerActions } = useMessageCenterTabLayer()
  const coversheetUploadRef = useRef<HTMLInputElement>(null)
  const attachmentInputRef = useRef<HTMLInputElement>(null)

  const reviewHtml = useMemo(
    () => sanitizeEmailHtml(previewContentToHtml(hooks.previewContent)),
    [hooks.previewContent],
  )
  const hasReviewContents =
    hooks.previewContent.kind === 'html' &&
    Boolean(hooks.previewContent.html?.trim())

  // Syncfusion has no built-in toolbar string "Blockquote" — only Formats → Quotation.
  const faxRteToolbar = useMemo(
    (): (string | IToolbarItems)[] => [
      'Undo',
      'Redo',
      'FontName',
      'Formats',
      'FontSize',
      'FontColor',
      'BackgroundColor',
      '|',
      'Alignments',
      {
        tooltipText: 'Blockquote',
        undo: true,
        template:
          '<button class="e-tbar-btn e-btn custom-toolbar-button" type="button" title="Blockquote" id="fax-compose-rte_toolbar_Blockquote"><span class="e-btn-icon e-icons mgc_quote_right_fill"></span></button>',
        click: () => {
          rteRef.current?.executeCommand('formatBlock', 'BlockQuote')
        },
      },
      '|',
      'Bold',
      'Italic',
      'Underline',
      'StrikeThrough',
    ],
    [rteRef],
  )

  const renderTemplateCard = (
    template: IFaxCoversheet,
    isCreate?: boolean,
  ): React.ReactNode => {
    if (isCreate) {
      return (
        <button
          type="button"
          key="create"
          className="unstyled-button fax-template-card create"
          onClick={() => void hooks.handleCreateNewTemplate()}
        >
          <span className="fax-template-create-icon mgc_add_circle_fill" />
          <span className="fax-template-body">
            <span className="fax-template-title">Create a new template</span>
            <span className="fax-template-desc">Create a custom template</span>
          </span>
        </button>
      )
    }
    const selected = hooks.selectedTemplateId === template.fax_coversheet_id
    return (
      <div
        key={template.fax_coversheet_id}
        className={`fax-template-card${selected ? ' selected' : ''}`}
        role="button"
        tabIndex={0}
        aria-label={template.file_name ?? undefined}
        onClick={() => void hooks.selectTemplate(template)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          e.preventDefault()
          void hooks.selectTemplate(template)
        }}
      >
        <FaxCoversheetThumbPreview template={template} />
        <div className="fax-template-body">
          <span className="fax-template-title">{template.file_name}</span>
          <span className="fax-template-desc">{template.description}</span>
        </div>
      </div>
    )
  }

  const updateField = (id: string, patch: Partial<CoversheetField>): void => {
    hooks.setCoverContent((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }))
  }

  const newChecklistOptionId = (): string =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`

  const addChecklistOption = (fieldId: string): void => {
    hooks.setCoverContent((prev) => ({
      ...prev,
      fields: prev.fields.map((f) =>
        f.id === fieldId
          ? {
              ...f,
              items: [
                ...(f.items ?? []),
                { id: newChecklistOptionId(), label: '', checked: false },
              ],
            }
          : f,
      ),
    }))
  }

  const removeChecklistOption = (fieldId: string, itemId: string): void => {
    hooks.setCoverContent((prev) => ({
      ...prev,
      fields: prev.fields.map((f) =>
        f.id === fieldId
          ? { ...f, items: (f.items ?? []).filter((it) => it.id !== itemId) }
          : f,
      ),
    }))
  }

  const handleAttachmentFiles = (files: FileList | null): void => {
    if (!files?.length) return
    for (const file of Array.from(files)) {
      void hooks.addLocalDocument(file)
    }
  }

  // Figma: Search + Skip/Upload stay in the top bar through Review (stage 4).
  // Step nav (Back/Next/etc.) lives in the compose footer from Cover onward.
  useEffect(() => {
    setTabLayerActions(
      <div className="fax-compose-actions">
        <SearchWithFilter
          placeholder="Search"
          filterIcon="mgc_settings_2_line"
          searchQuery={hooks.coversheetSearchQuery}
          onChange={(e) => hooks.setCoversheetSearchQuery(e.target.value)}
          filters={{
            type: ['Default', 'Custom'],
          }}
          filterLabels={{
            type: 'Type',
          }}
          selectedFilters={hooks.coversheetFilter}
          onFilterChange={hooks.handleFilterChange}
        />
        <Button
          className="button-secondary"
          onClick={() => void hooks.handleSkipCover()}
        >
          Skip cover sheet
        </Button>
        <Button
          className="button-primary"
          onClick={() => coversheetUploadRef.current?.click()}
        >
          Upload cover sheet
        </Button>
      </div>,
    )
    return () => setTabLayerActions(null)
  }, [
    hooks.coversheetSearchQuery,
    hooks.coversheetFilter,
    hooks.handleFilterChange,
    hooks.handleSkipCover,
    hooks.setCoversheetSearchQuery,
    setTabLayerActions,
  ])

  return (
    <div className="fax-compose-container">
      <input
        ref={coversheetUploadRef}
        type="file"
        accept={COVERSHEET_UPLOAD_ACCEPT}
        className="d-none"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            void hooks.handleUploadCoverSheet(file)
          }
          e.target.value = ''
        }}
      />
      <input
        ref={attachmentInputRef}
        type="file"
        multiple
        accept=".pdf,image/*"
        className="d-none"
        onChange={(e) => {
          handleAttachmentFiles(e.target.files)
          e.target.value = ''
        }}
      />
      {hooks.step > 0 && (
        <div className="fax-steps-wrapper">
          <Stepper steps={STEPS} currentStep={hooks.step} />
        </div>
      )}
      {hooks.step === 0 && (
        <div className="fax-templates-container">
          {hooks.showDefaultCoversheets && (
            <div className="fax-templates-section">
              <span className="fax-section-title">Default cover sheets</span>
              <div className="fax-template-grid">
                {hooks.systemTemplates.map((t) => renderTemplateCard(t))}
              </div>
            </div>
          )}
          {hooks.showCustomCoversheets && (
            <div className="fax-templates-section">
              <span className="fax-section-title">Custom cover sheets</span>
              <div className="fax-template-grid">
                {hooks.customTemplates.map((t) => renderTemplateCard(t))}
                {renderTemplateCard({} as IFaxCoversheet, true)}
              </div>
            </div>
          )}
        </div>
      )}
      {hooks.step === 1 && hooks.coversheetMode === 'template' && (
        <div className="fax-cover-editor-wrapper">
          <div ref={faxRteToolbarSlotRef} className="fax-rte-toolbar-slot" />
          <div className="fax-compose-step fax-cover-editor">
            <div className="fax-compose-coversheet">
              <span className="fax-new-cover-title">
                {hooks.coverContent.title || 'New cover sheet'}
              </span>
              <div className="fax-cover-row">
                <span className="fax-cover-label">To:</span>
                <Button
                  className={`button-secondary fax-pill-btn ${hooks.recipient ? 'readonly' : ''}`}
                  onClick={() => hooks.setShowRecipientModal(true)}
                >
                  {hooks.recipient
                    ? `${hooks.recipient.first_name} ${hooks.recipient.last_name}`
                    : 'Recipient name'}
                </Button>
                <Button
                  className="button-secondary fax-pill-btn"
                  onClick={() => hooks.setShowRecipientModal(true)}
                >
                  {hooks.recipient
                    ? (hooks.coverContent.to?.recipientFax ?? '')
                    : 'Recipient fax number'}
                </Button>
              </div>
              <div className="fax-cover-row">
                <span className="fax-cover-label">From:</span>
                <span className="fax-pill-btn readonly">
                  {hooks.coverContent.from?.displayName || '-'}
                </span>
                <span className="fax-pill-btn readonly">
                  {hooks.coverContent.from?.faxNumber || '-'}
                </span>
              </div>
              <div className="fax-cover-row">
                <span className="fax-cover-label">Subject:</span>
                <InputField
                  className="input-field-container-full"
                  placeholder="Input value"
                  value={hooks.coverContent.title ?? ''}
                  onChange={(e) =>
                    hooks.setCoverContent((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="fax-cover-row">
                <span className="fax-cover-label">Sent on:</span>
                <span className="fax-pill-btn readonly">
                  {hooks.coverContent.sentOn}
                </span>
              </div>
              {hooks.coverContent.fields.map((field) => (
                <div key={field.id} className="fax-cover-field-block">
                  {field.type === 'checklist' ? (
                    <div className="fax-checklist-box">
                      <div className="fax-checklist-item">
                        Checklist:{' '}
                        <InputField
                          className="input-field-container-full"
                          placeholder="Checklist name"
                          value={field.label ?? ''}
                          onChange={(e) =>
                            updateField(field.id, { label: e.target.value })
                          }
                        />
                        <button
                          type="button"
                          className="unstyled-button fax-remove-field-button mgc_delete_2_fill"
                          title="Remove checklist"
                          aria-label="Remove checklist"
                          onClick={() =>
                            hooks.setCoverContent((prev) => ({
                              ...prev,
                              fields: prev.fields.filter(
                                (f) => f.id !== field.id,
                              ),
                            }))
                          }
                        />
                      </div>
                      {(field.items ?? []).map((item, index) => (
                        <div
                          key={item.id}
                          className="fax-checklist-item fax-checklist-option"
                        >
                          <input
                            type="checkbox"
                            checked={item.checked ?? false}
                            onChange={(e) =>
                              hooks.setCoverContent((prev) => ({
                                ...prev,
                                fields: prev.fields.map((f) =>
                                  f.id === field.id
                                    ? {
                                        ...f,
                                        items: f.items?.map((it) =>
                                          it.id === item.id
                                            ? {
                                                ...it,
                                                checked: e.target.checked,
                                              }
                                            : it,
                                        ),
                                      }
                                    : f,
                                ),
                              }))
                            }
                          />
                          <InputField
                            placeholder={`Option ${index + 1}`}
                            value={item.label}
                            onChange={(e) =>
                              hooks.setCoverContent((prev) => ({
                                ...prev,
                                fields: prev.fields.map((f) =>
                                  f.id === field.id
                                    ? {
                                        ...f,
                                        items: f.items?.map((it) =>
                                          it.id === item.id
                                            ? {
                                                ...it,
                                                label: e.target.value,
                                              }
                                            : it,
                                        ),
                                      }
                                    : f,
                                ),
                              }))
                            }
                          />
                          <button
                            type="button"
                            className="unstyled-button fax-remove-field-button mgc_close_line"
                            title="Remove option"
                            aria-label="Remove option"
                            onClick={() =>
                              removeChecklistOption(field.id, item.id)
                            }
                          />
                        </div>
                      ))}
                      <Button
                        className="button-primary fax-checklist-add-option"
                        onClick={() => addChecklistOption(field.id)}
                      >
                        + Add option
                      </Button>
                    </div>
                  ) : (
                    <div className="fax-cover-row">
                      <InputField
                        className="fax-input-field-label"
                        placeholder="Input field name"
                        value={field.label ?? ''}
                        onChange={(e) =>
                          updateField(field.id, { label: e.target.value })
                        }
                      />
                      <span>:</span>
                      {field.type === 'date' ? (
                        <DatePicker
                          id={field.id}
                          format="MMMM dd, yyyy"
                          className="fax-input-field-value"
                          placeholder="Select date"
                          value={field.dateValue ?? ''}
                          onDateChange={(e) =>
                            updateField(field.id, { dateValue: e.value })
                          }
                        />
                      ) : (
                        <InputField
                          className="fax-input-field-value"
                          placeholder="Input value"
                          value={field.value ?? ''}
                          onChange={(e) =>
                            updateField(field.id, { value: e.target.value })
                          }
                        />
                      )}
                      <button
                        type="button"
                        className="unstyled-button fax-remove-field-button mgc_delete_2_fill"
                        title="Remove field"
                        aria-label="Remove field"
                        onClick={() =>
                          hooks.setCoverContent((prev) => ({
                            ...prev,
                            fields: prev.fields.filter(
                              (f) => f.id !== field.id,
                            ),
                          }))
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
              <div className="fax-insert-field-trigger">
                <Button
                  className="button-primary add-field-button"
                  onClick={() =>
                    hooks.setShowInsertFieldModal(!hooks.showInsertFieldModal)
                  }
                >
                  + Insert new fields
                </Button>
                <InsertFieldModal
                  show={hooks.showInsertFieldModal}
                  onHide={() => hooks.setShowInsertFieldModal(false)}
                  onInsert={hooks.insertField}
                />
              </div>
              <div className="fax-cover-message">
                <strong>Message:</strong>
                <div className="fcc-rte-container">
                  <RichTextEditorComponent
                    ref={rteRef as React.RefObject<RichTextEditorComponent>}
                    id="fax-compose-rte"
                    className="fcc-message-input"
                    value={hooks.coverContent.messageHtml ?? ''}
                    placeholder="Write here..."
                    toolbarSettings={{ items: faxRteToolbar }}
                    created={handleFaxRteCreated}
                    change={(args) => {
                      const text =
                        rteRef.current?.getText() ??
                        args.value.replace(/<[^>]+>/g, '')
                      const html = rteRef.current?.getHtml() ?? args.value
                      hooks.handleMessageChange(html, text)
                    }}
                  >
                    <Inject
                      services={[Toolbar, HtmlEditor, Link, QuickToolbar]}
                    />
                  </RichTextEditorComponent>
                </div>
              </div>
            </div>
          </div>
          <div className="fax-compose-footer">
            <Button className="button-secondary" onClick={hooks.handleBack}>
              Back
            </Button>
            <Button
              className="button-secondary"
              onClick={() => void hooks.handleSaveTemplate()}
            >
              Save as template
            </Button>
            <Button
              className="button-primary"
              onClick={() => void hooks.handleNext()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {hooks.step === 2 && (
        <div className="fax-compose-step">
          <div
            className="fax-documents-row"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault()
              handleAttachmentFiles(e.dataTransfer.files)
            }}
          >
            {hooks.coversheetMode !== 'skipped' && (
              <div className="fax-doc-card preview">
                {hooks.coversheetMode === 'uploaded'
                  ? (hooks.uploadedCoversheetFile?.name ?? 'Cover sheet')
                  : 'Cover sheet'}
              </div>
            )}
            {hooks.documents.map((doc, index) => (
              <FaxDocumentPagePreview
                key={doc.localId}
                file={doc.file}
                fileUrl={doc.fileUrl}
                fileName={doc.file_name}
                mimeType={doc.mime_type}
                onRemove={() => void hooks.removeDocument(doc.localId)}
                onLoadStart={hooks.beginPreviewLoading}
                onLoadEnd={hooks.endPreviewLoading}
                draggable
                isDragging={dragIndex === index}
                isDragOver={dragOverIndex === index && dragIndex !== index}
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOverIndex((prev) => (prev === index ? prev : index))
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (dragIndex !== null && dragIndex !== index) {
                    hooks.reorderDocuments(dragIndex, index)
                  }
                  resetDocumentDrag()
                }}
                onDragEnd={resetDocumentDrag}
              />
            ))}
            <div
              className="fax-doc-upload-card"
              role="button"
              tabIndex={0}
              aria-label="Upload documents"
              onClick={() => attachmentInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return
                if (e.target !== e.currentTarget) return
                e.preventDefault()
                attachmentInputRef.current?.click()
              }}
            >
              <span className="upload-icon mgc_arrow_up_line" />
              <span>
                Drag and drop
                <br /> or click to upload documents
              </span>
              <Button
                className="button-secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  attachmentInputRef.current?.click()
                }}
              >
                Click here
              </Button>
            </div>
          </div>
          <div className="fax-compose-footer">
            <Button className="button-secondary" onClick={hooks.handleBack}>
              Back
            </Button>
            <Button
              className="button-primary"
              onClick={() => void hooks.handleNext()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {hooks.step === 3 && (
        <div className="fax-compose-step">
          <div className="fax-review-step">
            <div className="fax-review-main">
              {hasReviewContents ? (
                <div
                  className="fax-review-preview"
                  dangerouslySetInnerHTML={{
                    __html: reviewHtml,
                  }}
                />
              ) : (
                <EmptyState
                  className="fax-contents-empty"
                  iconClass="mgc mgc_fax_fill"
                  title="No contents to show"
                  description={
                    hooks.previewContent.kind === 'not_available'
                      ? 'Preview is not available for this coversheet.'
                      : 'This fax has no coversheet content.'
                  }
                />
              )}
            </div>
            <div className="fax-review-attachments">
              <h4>Attachments:</h4>
              {hooks.documents.length === 0 ? (
                <EmptyState
                  className="fax-attachments-empty"
                  iconClass="mgc mgc_attachment_fill"
                  title="No attachments"
                  description="There are no files attached to this fax."
                />
              ) : (
                hooks.documents.map((doc) => (
                  <div key={doc.localId} className="fax-attachment-card">
                    <div>
                      <div className="fax-attachment-title">
                        {doc.file_name}{' '}
                        <span className="fax-attachment-download-icon mgc_download_2_fill" />
                      </div>
                      <div className="fax-attachment-meta">
                        {doc.mime_type?.includes('pdf') ? 'PDF file' : 'File'}
                        {doc.file_size
                          ? ` - ${formatFileSize(doc.file_size)}`
                          : ''}
                      </div>
                      <FaxDocumentPagePreview
                        file={doc.file}
                        fileUrl={doc.fileUrl}
                        fileName={doc.file_name}
                        mimeType={doc.mime_type}
                        variant="stacked"
                        onLoadStart={hooks.beginPreviewLoading}
                        onLoadEnd={hooks.endPreviewLoading}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="fax-compose-footer">
            <Button
              className="button-secondary"
              onClick={() => hooks.setShowRecipientModal(true)}
            >
              {hooks.recipientActionLabel}
            </Button>
            <Button className="button-secondary" onClick={hooks.handleBack}>
              Back
            </Button>
            <Button
              className="button-primary"
              onClick={() => void hooks.handleSend()}
            >
              Send fax
            </Button>
          </div>
        </div>
      )}
      <SelectFaxRecipientModal
        show={hooks.showRecipientModal}
        contacts={hooks.contacts}
        currentContact={hooks.recipient}
        currentDestination={hooks.recipientDestination}
        onHide={() => hooks.setShowRecipientModal(false)}
        onSelect={hooks.setRecipientContact}
      />
    </div>
  )
}

export default FaxCompose
